// ReportingPin — the pinned "Reporting" row anchored under the Dream Team pin at the bottom of the
// sidebar. Shown to anyone whose channel list contains the Reporting channel (staff implicitly, clients
// once added in Channel settings). Uses the channel's LIVE name so a rename shows immediately.
// Right-click / long-press opens the same style of context menu the other rows have (Summarize unread,
// Mute, and for staff a Rename shortcut into Channel settings).
import { useRef, useState } from "react";
import Icon from "./Icon.jsx";

// The emblem — a small bar chart with a rising line, in the same tile shape as DreamTeamLogo.
export function ReportingLogo({ size = 44 }) {
  return (
    <span className="dt-logo rp-logo" style={{ width: size, height: size }} aria-hidden>
      <svg className="dt-logo__svg" viewBox="0 0 48 48">
        <rect className="rp-logo__bar" x="9" y="26" width="7" height="14" rx="1.5" fill="#9ff29a" style={{ animationDelay: "0s" }} />
        <rect className="rp-logo__bar" x="20.5" y="19" width="7" height="21" rx="1.5" fill="#dff7c9" style={{ animationDelay: ".4s" }} />
        <rect className="rp-logo__bar" x="32" y="12" width="7" height="28" rx="1.5" fill="#ffffff" style={{ animationDelay: ".8s" }} />
        <polyline points="8,24 19,18 30,13 41,7" fill="none" stroke="#ffd7ac" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="41" cy="7" r="2.4" fill="#ffd7ac" />
      </svg>
    </span>
  );
}

export default function ReportingPin({ channel, activeId, onSelect, isStaff = false, onSummarizeUnread, onMute, onSettings }) {
  if (!channel) return null;
  const active = channel.id === activeId;
  const unread = channel.unreadCount ?? channel.unread ?? 0;
  const muted = channel.isMuted ?? channel.muted ?? false;

  const [menu, setMenu] = useState(null); // { x, y } | null
  const pressTimer = useRef(null);
  const suppressClick = useRef(false);
  const openMenu = (x, y) => setMenu({ x: Math.min(x, window.innerWidth - 190), y });
  const closeMenu = () => setMenu(null);
  const startPress = (e) => {
    if (e.pointerType === "mouse") return; // desktop uses onContextMenu
    suppressClick.current = false;
    pressTimer.current = setTimeout(() => { suppressClick.current = true; openMenu(e.clientX, e.clientY); }, 500);
  };
  const cancelPress = () => { if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; } };
  const click = () => { if (suppressClick.current) { suppressClick.current = false; return; } onSelect(channel); };

  return (
    <div className="dt-pin-wrap dt-pin-wrap--reporting" data-tour="reporting">
      <div className="dt-pin-label"><span className="dt-pin-dot dt-pin-dot--reporting" />Reporting · Pinned</div>
      <button className={`dt-pin dt-pin--reporting ${active ? "dt-pin--active" : ""}`} onClick={click}
              onContextMenu={(e) => { e.preventDefault(); openMenu(e.clientX, e.clientY); }}
              onPointerDown={startPress} onPointerUp={cancelPress} onPointerLeave={cancelPress} onPointerCancel={cancelPress}>
        <span className="dt-pin__sheen" aria-hidden />
        <ReportingLogo size={46} />
        <span className="dt-pin__meta">
          <span className="dt-pin__name">{channel.name || "Reporting"}</span>
          <span className="dt-pin__sub">Adscope marketing reports</span>
        </span>
        {muted && <span className="channel__mute">🔕</span>}
        {unread > 0 && !active && <span className="unread dt-pin__unread">{unread}</span>}
      </button>

      {menu && (
        <>
          <div className="chan-menu__overlay" onClick={closeMenu} onContextMenu={(e) => { e.preventDefault(); closeMenu(); }} />
          <div className="chan-menu" style={{ left: menu.x, top: menu.y }}>
            {isStaff && unread > 10 && (
              <button className="chan-menu__item" onClick={() => { onSummarizeUnread?.(channel); closeMenu(); }}>
                <Icon name="refresh" className="ic--sm" />
                Summarize {unread} unread
              </button>
            )}
            <button className="chan-menu__item" onClick={() => { onMute?.(channel, !muted); closeMenu(); }}>
              <Icon name={muted ? "volume" : "volume-x"} className="ic--sm" />
              {muted ? "Unmute channel" : "Mute channel"}
            </button>
            {isStaff && onSettings && (
              <button className="chan-menu__item" onClick={() => { onSettings(channel); closeMenu(); }}>
                <Icon name="edit" className="ic--sm" />
                Rename channel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
